/**
 * Class comparison: URL params and client-side aggregation over the summary file.
 *
 * compare=last5 | 2020,2021,2022   (default last 5, relative to CURRENT_YEAR)
 * align=year | cumulative | season (default year = NFL year / rookie vs rookie)
 */

import { CURRENT_YEAR, FIRST_YEAR } from '../constants/app';
import type { DraftRoundWorkerParams } from './draftRoundFilter';
import {
  ClassSummaryFile,
  ClassSummaryRow,
  PLAYER,
  ROW,
  rowFantasyPoints,
} from '../types/classSummary';
import { resolveCollegeMeta, resolveConferenceMeta } from './collegeMeta';
import { matchesFilters } from './filterUtils';

export const COMPARE_PARAM = 'compare';
export const ALIGN_PARAM = 'align';
export const CLASS_STAT_PARAM = 'stat';
export const DISPLAY_PARAM = 'display';

export const CLASS_DISPLAY_MODES = ['table', 'charts'] as const;
export type ClassDisplayMode = (typeof CLASS_DISPLAY_MODES)[number];

export const parseClassDisplayParam = (
  value: string | null | undefined
): ClassDisplayMode => {
  const trimmed = value?.trim().toLowerCase();
  if (trimmed === 'charts' || trimmed === 'chart') return 'charts';
  return 'table';
};

export const CLASS_STAT_CATEGORIES = [
  '',
  'passing',
  'rushing',
  'receiving',
  'defense',
  'kicking',
  'punting',
  'returning',
] as const;

export type ClassStatCategory = (typeof CLASS_STAT_CATEGORIES)[number];

const STAT_TO_ROW: Record<Exclude<ClassStatCategory, ''>, number> = {
  passing: ROW.pass,
  rushing: ROW.rush,
  receiving: ROW.rec,
  defense: ROW.def,
  kicking: ROW.kick,
  punting: ROW.punt,
  returning: ROW.ret,
};

export const parseClassStatParam = (value: string | null | undefined): ClassStatCategory => {
  if (!value) return '';
  const trimmed = value.trim().toLowerCase();
  return CLASS_STAT_CATEGORIES.includes(trimmed as ClassStatCategory)
    ? (trimmed as ClassStatCategory)
    : '';
};

export const describeClassStat = (stat: ClassStatCategory): string => {
  if (!stat) return 'fantasy points';
  if (stat === 'defense') return 'defensive fantasy points';
  return `${stat} fantasy points`;
};

export const COMPARE_PRESETS = [3, 5, 10] as const;
export const DEFAULT_COMPARE_COUNT = 5;
export const MAX_COMPARE_CLASSES = 10;

export type ClassCompareSelection =
  | { kind: 'last'; count: number }
  | { kind: 'years'; years: number[] };

export type ClassAlignMode = 'year' | 'cumulative' | 'season';

export interface ClassCompareFilters {
  positions: string[] | null;
  draft: DraftRoundWorkerParams | null;
  stat?: ClassStatCategory;
  /** College or conference name chips (`?filter=`), same matching as rankings. */
  names?: string[];
}

export interface ClassYearSeries {
  classYear: number;
  /** Per-column fantasy points (NFL year or calendar season). */
  values: number[];
  /** Running sum of `values`. */
  cumulative: number[];
  total: number;
  /** True when this class is missing early NFL years (class year before FIRST_YEAR). */
  partial: boolean;
}

export interface ClassCompareTable {
  align: ClassAlignMode;
  columns: number[];
  series: ClassYearSeries[];
  ytdWeek: number;
  currentSeasonIncomplete: boolean;
  currentSeason: number;
}

const isClassYear = (value: number): boolean =>
  Number.isInteger(value) && value >= 1900 && value <= 2100;

export const parseCompareParam = (value: string | null | undefined): ClassCompareSelection | null => {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  const last = trimmed.match(/^last(\d+)$/);
  if (last) {
    const count = parseInt(last[1], 10);
    return COMPARE_PRESETS.includes(count as (typeof COMPARE_PRESETS)[number])
      ? { kind: 'last', count }
      : null;
  }

  const years = trimmed
    .split(',')
    .map((token) => parseInt(token.trim(), 10))
    .filter(isClassYear);
  const unique = Array.from(new Set(years)).sort((a, b) => a - b);
  if (!unique.length || unique.length > MAX_COMPARE_CLASSES) return null;
  return { kind: 'years', years: unique };
};

export const serializeCompareSelection = (selection: ClassCompareSelection): string => {
  if (selection.kind === 'last') return `last${selection.count}`;
  return selection.years.join(',');
};

export const parseAlignParam = (value: string | null | undefined): ClassAlignMode => {
  if (value === 'cumulative' || value === 'season') return value;
  return 'year';
};

/** Years to compare. `lastN` is relative to the current season. */
export const resolveCompareYears = (
  selection: ClassCompareSelection | null,
  currentYear: number = CURRENT_YEAR
): number[] => {
  if (!selection || selection.kind === 'last') {
    const count = selection?.count ?? DEFAULT_COMPARE_COUNT;
    const years: number[] = [];
    for (let i = count - 1; i >= 0; i--) years.push(currentYear - i);
    return years;
  }
  return selection.years;
};

export const describeCompareSelection = (
  selection: ClassCompareSelection | null,
  currentYear: number = CURRENT_YEAR
): string => {
  const years = resolveCompareYears(selection, currentYear);
  if (!selection || selection.kind === 'last') {
    const count = selection?.count ?? DEFAULT_COMPARE_COUNT;
    return `Last ${count} classes (${years[0]}–${years[years.length - 1]})`;
  }
  if (years.length === 1) return `Class of ${years[0]}`;
  return `Classes ${years.join(', ')}`;
};

export const nflYearFor = (season: number, classYear: number): number => season - classYear + 1;

const matchesDraft = (round: number | null, draft: DraftRoundWorkerParams): boolean => {
  if (round == null) return Boolean(draft.undrafted);
  return draft.roundFrom != null && draft.roundTo != null && round >= draft.roundFrom && round <= draft.roundTo;
};

export const classRowMatchesNames = (
  collegeId: string,
  names: string[] | null | undefined
): boolean => {
  if (!names || names.length === 0) return true;
  const college = resolveCollegeMeta(collegeId);
  const conference = resolveConferenceMeta(college.conferenceId);
  return matchesFilters(
    {
      college: { displayName: college.name },
      conference: conference.name,
      name: college.slug,
    },
    names
  );
};

export const rowMatchesClassFilters = (
  row: ClassSummaryRow,
  playerRound: number | null,
  filters: ClassCompareFilters | null
): boolean => {
  if (!filters) return true;
  if (filters.positions && !filters.positions.includes(row[ROW.pos] || '')) return false;
  if (filters.draft && !matchesDraft(playerRound, filters.draft)) return false;
  if (!classRowMatchesNames(String(row[ROW.collegeId]), filters.names)) return false;
  return true;
};

/** Incomplete current season only. Finished seasons always use the full total. */
export const useYtdForRow = (row: ClassSummaryRow, summary: ClassSummaryFile): boolean =>
  Boolean(summary.currentSeasonIncomplete && row[ROW.season] === summary.toYear);

export const classRowPoints = (row: ClassSummaryRow, summary: ClassSummaryFile): number =>
  useYtdForRow(row, summary) ? row[ROW.ytdPoints] : rowFantasyPoints(row);

/** Category column for a named stat. Current-season category totals are already through week W. */
export const classRowStatPoints = (
  row: ClassSummaryRow,
  summary: ClassSummaryFile,
  stat: ClassStatCategory = ''
): number => {
  if (!stat) return classRowPoints(row, summary);
  const value = row[STAT_TO_ROW[stat]];
  return typeof value === 'number' ? value : 0;
};

const columnValue = (values: Map<number, number>, key: number): number =>
  parseFloat((values.get(key) || 0).toFixed(2));

const withCumulative = (
  classYear: number,
  columns: number[],
  valuesByCol: Map<number, number>,
  partial: boolean
): ClassYearSeries => {
  const values = columns.map((col) => columnValue(valuesByCol, col));
  const cumulative: number[] = [];
  values.reduce((sum, value) => {
    const next = parseFloat((sum + value).toFixed(2));
    cumulative.push(next);
    return next;
  }, 0);
  return {
    classYear,
    values,
    cumulative,
    total: cumulative.length ? cumulative[cumulative.length - 1] : 0,
    partial,
  };
};

/**
 * Aggregate the summary into one series per compared class.
 * Partial classes (class year before FIRST_YEAR) are dropped in NFL-year modes.
 */
export const buildClassCompareTable = (
  summary: ClassSummaryFile,
  compareYears: number[],
  align: ClassAlignMode,
  filters: ClassCompareFilters | null = null,
  firstYear: number = FIRST_YEAR
): ClassCompareTable => {
  const years = Array.from(new Set(compareYears)).sort((a, b) => a - b);
  const includePartial = align === 'season';
  const visibleYears = years.filter((classYear) => includePartial || classYear >= firstYear);

  const totals = new Map<number, Map<number, number>>();
  visibleYears.forEach((classYear) => totals.set(classYear, new Map()));

  let maxNflYear = 1;
  const seasonKeys = new Set<number>();

  for (const row of summary.rows) {
    const player = summary.players[row[ROW.playerIndex]];
    if (!player) continue;
    const classYear = player[PLAYER.classYear];
    if (classYear == null || !totals.has(classYear)) continue;
    if (!rowMatchesClassFilters(row, player[PLAYER.round], filters)) continue;

    const season = row[ROW.season];
    const nflYear = nflYearFor(season, classYear);
    if (nflYear < 1) continue;
    if (nflYear > maxNflYear) maxNflYear = nflYear;
    seasonKeys.add(season);

    const points = classRowStatPoints(row, summary, filters?.stat || '');
    const key = align === 'season' ? season : nflYear;
    const bucket = totals.get(classYear) as Map<number, number>;
    bucket.set(key, (bucket.get(key) || 0) + points);
  }

  const columns =
    align === 'season'
      ? Array.from(seasonKeys).sort((a, b) => a - b)
      : Array.from({ length: maxNflYear }, (_, i) => i + 1);

  const nameFilters = filters?.names || [];
  const series = visibleYears
    .map((classYear) =>
      withCumulative(classYear, columns, totals.get(classYear) as Map<number, number>, classYear < firstYear)
    )
    .filter((item) => !nameFilters.length || item.total > 0)
    .sort((a, b) => b.total - a.total || a.classYear - b.classYear);

  return {
    align,
    columns,
    series,
    ytdWeek: summary.ytdWeek,
    currentSeasonIncomplete: summary.currentSeasonIncomplete,
    currentSeason: summary.toYear,
  };
};

/** True when this cell is the unfinished current season. */
export const cellUsesYtd = (table: ClassCompareTable, classYear: number, columnIndex: number): boolean => {
  if (!table.currentSeasonIncomplete) return false;
  const column = table.columns[columnIndex];
  if (table.align === 'season') return column === table.currentSeason;
  return nflYearFor(table.currentSeason, classYear) === column;
};

export const columnLabel = (align: ClassAlignMode, column: number): string => {
  if (align === 'season') return String(column);
  return `Y${column}`;
};
